import React, { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, ArrowRight, FileText } from "lucide-react";
import { getPostBySlug, getAllPosts, catSlug } from "@/content/blog";
import { getCaseStudyForArticle } from "@/content/case-studies";
import Seo from "@/lib/Seo";

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);

  useEffect(() => {
    if (post) document.title = `${post.meta.title} · Steady Path Funding`;
    return () => { document.title = "Steady Path Funding"; };
  }, [post]);

  if (!post) {
    return (
      <div className="pt-40 pb-32 text-center px-6">
        <div className="overline">404</div>
        <h1 className="mt-6 font-serif text-5xl">Article not found.</h1>
        <button onClick={() => navigate("/blog")} className="mt-8 gold-link font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]">
          Back to blog →
        </button>
      </div>
    );
  }

  const { meta, body } = post;
  const all = getAllPosts();
  const relatedCaseStudy = getCaseStudyForArticle(meta.slug);

  // Related articles: prefer 3 from the SAME category, fall back to recent others if fewer.
  const sameCategory = all.filter(
    (p) => p.slug !== meta.slug && p.category === meta.category
  );
  const fallback = all.filter((p) => p.slug !== meta.slug && p.category !== meta.category);
  const related = [...sameCategory, ...fallback].slice(0, 3);
  const categorySlug = catSlug(meta.category);

  // Keep datePublished in structured data for SEO; hide from UI to feel evergreen.
  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": meta.title,
    "description": meta.excerpt,
    "image": meta.image,
    "datePublished": meta.date,
    "author": { "@type": "Organization", "name": "Steady Path Funding" },
    "publisher": {
      "@type": "Organization",
      "name": "Steady Path Funding",
      "logo": { "@type": "ImageObject", "url": "https://www.steadypathfunding.com/favicon.svg" },
    },
    "articleSection": meta.category,
  };

  return (
    <article>
      <Seo
        path={`/blog/${meta.slug}`}
        title={meta.title}
        description={meta.excerpt}
        image={meta.image}
        type="article"
        publishedTime={meta.date}
        jsonLd={articleLd}
      />
      {/* Hero */}
      <section className="pt-36 pb-12 border-b border-black/10">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10">
          <Link to="/blog" className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/60 hover:text-[color:var(--gold)] transition-colors mb-10">
            <ArrowLeft size={14} /> All articles
          </Link>
          <div className="overline">
            <Link
              to={`/blog/category/${categorySlug}`}
              data-testid="post-category-link"
              className="hover:underline"
            >
              {meta.category}
            </Link>
            {" · "}
            {meta.readTime}
          </div>
          <h1 className="mt-6 font-serif text-4xl md:text-6xl leading-[1.02] tracking-tight">
            {meta.title}
          </h1>
          <p className="mt-8 text-[color:var(--bone)]/75 text-lg leading-relaxed max-w-2xl">
            {meta.excerpt}
          </p>
          <div className="mt-10 flex items-center gap-4 font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/55">
            <span>Steady Path Editorial</span>
            <span className="text-[color:var(--gold)]">·</span>
            <span>{meta.readTime}</span>
          </div>
        </div>
      </section>

      {/* Cover */}
      <section className="border-b border-black/10">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-12 md:py-16">
          <div className="relative aspect-[16/9] overflow-hidden">
            <img src={meta.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/12" />
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16 md:py-24">
        <div className="max-w-[760px] mx-auto px-6 md:px-10">
          <div className="prose-editorial">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
          </div>

          {/* Related deal structure — appears only when a study lists this
              article in its relatedArticles map. Positioned mid-content so
              readers see an illustrative walkthrough before hitting the CTA.
              Note: these are illustrative, not specific closings — the
              disclaimer under the amount makes that explicit. */}
          {relatedCaseStudy && (
            <aside
              data-testid="blog-related-case-study"
              className="mt-16 border border-[color:var(--gold)]/30 bg-[color:var(--bg-elev)] p-6 md:p-8"
            >
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase text-[color:var(--gold)]">
                <FileText size={12} /> Deal structure · How this gets built
              </div>
              <Link
                to={`/deal-structures/${relatedCaseStudy.slug}`}
                className="group block mt-4"
              >
                <div className="flex items-baseline justify-between gap-4 flex-wrap mb-3">
                  <div className="font-serif text-4xl md:text-5xl text-[color:var(--gold)] leading-none">
                    {relatedCaseStudy.amount}
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-[color:var(--bone)]/60">
                    Funded in {relatedCaseStudy.timeToFund}
                  </div>
                </div>
                <h3 className="font-serif text-2xl md:text-3xl leading-tight group-hover:text-[color:var(--gold)] transition-colors">
                  {relatedCaseStudy.title}
                </h3>
                <p className="mt-4 text-[color:var(--bone)]/75 leading-relaxed">
                  {relatedCaseStudy.excerpt}
                </p>
                <span className="mt-5 inline-flex items-center gap-2 font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]">
                  See the illustrative walkthrough <ArrowRight size={14} />
                </span>
              </Link>
              <div className="mt-5 pt-4 border-t border-black/10 font-mono text-[9px] tracking-[0.22em] uppercase text-[color:var(--bone)]/40">
                Illustrative walkthrough · Not a specific closed transaction
              </div>
            </aside>
          )}

          <div className="mt-20 pt-10 border-t border-black/10 flex items-center justify-between gap-6 flex-wrap">
            <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
              Ready to apply? Takes four minutes.
            </div>
            <Link to="/apply" className="inline-flex items-center gap-2 bg-[color:var(--gold)] text-black px-6 py-3 font-mono text-xs tracking-[0.24em] uppercase hover:bg-[color:var(--gold-strong)] transition-colors">
              Start application <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Related — same category first */}
      {related.length > 0 && (
        <section className="py-16 md:py-24 border-t border-black/10 bg-[color:var(--surface)]">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="flex items-baseline justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="overline">More on {meta.category.toLowerCase()}</div>
                <h2 className="mt-3 font-serif text-3xl md:text-4xl leading-tight">
                  Keep reading the journal
                </h2>
              </div>
              <Link
                to={`/blog/category/${categorySlug}`}
                data-testid="related-view-all"
                className="gold-link font-mono text-xs tracking-[0.24em] uppercase text-[color:var(--gold)]"
              >
                View all {meta.category} articles →
              </Link>
            </div>
            <div className="grid grid-cols-12 gap-8">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to={`/blog/${p.slug}`}
                  data-testid={`related-post-${p.slug}`}
                  className="col-span-12 md:col-span-4 group border-t border-black/10 pt-8"
                >
                  <div className="relative aspect-[4/3] overflow-hidden mb-5">
                    <img
                      src={p.image}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                    />
                    <div className="absolute inset-0 bg-black/15" />
                  </div>
                  <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-[color:var(--bone)]/50">
                    {p.category} · {p.readTime}
                  </div>
                  <h3 className="mt-3 font-serif text-2xl md:text-3xl leading-tight group-hover:text-[color:var(--gold)] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-[color:var(--bone)]/70 text-sm leading-relaxed">{p.excerpt}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </article>
  );
}
